// src/utils/payrollPdfExport.js
// Exports a Payslip PDF matching the exact template layout and visual design of the generated Payslip modal.

import jsPDF from "jspdf";
import html2canvas from "html2canvas";

const COMPANY_NAME = "Samriddhi Anveshana Pvt Ltd";
const COMPANY_ADDRESS = "705, Workafella Cyber Crown HiTech City, Hyderabad Telangana, India";

/* ── Indian-numbering-system number → words ─── */
function numberToWordsIndian(num) {
  num = Math.round(Number(num) || 0);
  if (num === 0) return "Zero";

  const ones = [
    "", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine", "Ten",
    "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen",
    "Eighteen", "Nineteen",
  ];
  const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];

  const twoDigits = (n) => (n < 20 ? ones[n] : tens[Math.floor(n / 10)] + (n % 10 ? " " + ones[n % 10] : ""));
  const threeDigits = (n) =>
    n < 100 ? twoDigits(n) : ones[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + twoDigits(n % 100) : "");

  let n = num;
  let result = "";
  const crore = Math.floor(n / 10000000); n %= 10000000;
  const lakh = Math.floor(n / 100000); n %= 100000;
  const thousand = Math.floor(n / 1000); n %= 1000;
  const hundred = n;

  if (crore) result += threeDigits(crore) + " Crore ";
  if (lakh) result += threeDigits(lakh) + " Lakh ";
  if (thousand) result += threeDigits(thousand) + " Thousand ";
  if (hundred) result += threeDigits(hundred);

  return result.trim();
}

function formatDate(val) {
  if (!val) return "NA";
  if (val?.toDate) return val.toDate().toLocaleDateString("en-GB");
  if (val?.seconds) return new Date(val.seconds * 1000).toLocaleDateString("en-GB");
  const d = new Date(val);
  return isNaN(d.getTime()) ? "NA" : d.toLocaleDateString("en-GB", { day: "2-digit", month: "2-digit", year: "numeric" });
}

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export async function generatePayslipPDF(payment, employee = null) {
  const empName = payment.employeeName || employee?.displayName || employee?.name || "Employee";
  const empId = payment.customEmployeeId || employee?.employeeId || employee?.empId || employee?.customId || payment.employeeId || "—";
  const department = payment.department || employee?.department || employee?.dept || "Engineering";
  const designation = payment.designation || employee?.designation || employee?.role || payment.role || "Staff";
  const bankAccountNo = (payment.bankAccountNo && payment.bankAccountNo !== "NA" ? payment.bankAccountNo : (employee?.accountNumber || employee?.bankAccountNo || employee?.bankAccountNumber || "NA"));
  const pfNumber = (payment.pfNumber && payment.pfNumber !== "NA" ? payment.pfNumber : (employee?.pfNumber || employee?.pfNo || employee?.uan || "NA"));
  const dateOfJoining = payment.joiningDate || employee?.joiningDate || employee?.dateOfJoining || employee?.internshipStartDate || null;
  const month = payment.month || (payment.monthNumber ? new Date(0, payment.monthNumber - 1).toLocaleString('default', { month: 'long' }) : "—");
  const year = payment.year || new Date().getFullYear();
  const workedDays = payment.workedDays ?? payment.presentDays ?? "30";

  // Earnings
  const basic = Number(payment.basic ?? payment.baseAmount ?? 0);
  const hra = Number(payment.hra ?? 0);
  const specialAllowance = Number(payment.specialAllowance ?? 0);
  const travelAllowance = Number(payment.travelAllowance ?? 0);
  const bonusIncentive = Number(payment.bonusIncentive ?? 0);
  const otherAllowance = Number(payment.otherAllowance ?? 0);
  const gross = Number(payment.gross ?? payment.grossPay ?? (basic + hra + specialAllowance + travelAllowance + bonusIncentive + otherAllowance));

  // Deductions
  const pf = Number(payment.pf ?? payment.pfDeduction ?? 0);
  const esi = Number(payment.esi ?? 0);
  const pt = Number(payment.professionalTax ?? 0);
  const incomeTax = Number(payment.incomeTax ?? 0);
  const attendanceLopDeduction = Number(payment.attendanceLopDeduction ?? payment.halfDayDeduction ?? payment.lopDeduction ?? 0);
  const absentDays = Number(payment.absentDays ?? 0);
  const halfDayCount = Number(payment.halfDayCount ?? 0);
  const otherDeductions = Number(payment.otherDeductions ?? 0);
  const totalDeductions = Number(payment.deductions ?? (pf + esi + pt + incomeTax + attendanceLopDeduction + otherDeductions));

  // Net
  const net = Number(payment.net ?? payment.netPay ?? Math.max(0, gross - totalDeductions));

  const isPaid = payment.status === "Paid" || payment.paymentStatus === "Paid" || payment.status === "Shared";
  const paidDate = payment.paidAt ? formatDate(payment.paidAt) : (isPaid ? "Completed" : "Pending");

  // Create temporary container for pixel-perfect PDF rendering
  const container = document.createElement("div");
  container.style.position = "fixed";
  container.style.top = "-9999px";
  container.style.left = "-9999px";
  container.style.width = "750px";
  container.style.backgroundColor = "#ffffff";
  container.style.fontFamily = "Arial, Helvetica, sans-serif";
  container.style.color = "#111111";
  container.style.padding = "24px";
  container.style.boxSizing = "border-box";

  container.innerHTML = `
    <div style="padding: 0 0 16px; font-family: Arial, Helvetica, sans-serif; color: #111;">
      <!-- Header -->
      <div style="display: flex; align-items: center; justify-content: space-between; padding: 0 0 14px 0; border-bottom: 2px solid #1a6f4e; margin-bottom: 14px;">
        <div style="display: flex; align-items: center; gap: 14px;">
          <div style="height: 52px; width: 52px; border-radius: 8px; background: #1a6f4e; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 18px;">
            SA
          </div>
          <div>
            <h1 style="margin: 0; font-size: 19px; color: #1a6f4e; font-weight: 800;">
              ${COMPANY_NAME}
            </h1>
            <p style="margin: 3px 0 0; font-size: 11px; color: #475569;">
              ${COMPANY_ADDRESS}
            </p>
          </div>
        </div>
        <div style="text-align: right;">
          <span style="display: inline-block; padding: 4px 12px; background: #1a6f4e; color: #fff; font-weight: 700; font-size: 12px; border-radius: 4px; letter-spacing: 0.05em;">
            PAYSLIP
          </span>
          <div style="font-size: 12px; color: #334155; font-weight: 700; margin-top: 4px;">
            ${month} ${year}
          </div>
        </div>
      </div>

      <!-- Payslip Main Table -->
      <table style="width: 100%; border-collapse: collapse; margin: 0;">
        <tbody>
          <!-- Row 1: Employee Name & Emp ID -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; width: 25%; font-weight: 600;">Employee Name</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; width: 25%; font-weight: 700; color: #111827;">${empName}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; width: 25%; font-weight: 600;">Emp ID</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; width: 25%; font-weight: 700; color: #1e3a8a;">${empId}</td>
          </tr>

          <!-- Row 2: Designation & Department -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Designation</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; color: #374151;">${designation}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Department</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; color: #374151;">${department}</td>
          </tr>

          <!-- Row 3: Worked Days & Date of Joining -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Worked Days</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 700; color: #166534;">${workedDays}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Date of Joining</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; color: #374151;">${formatDate(dateOfJoining)}</td>
          </tr>

          <!-- Row 4: Bank A/C No. & PF Number -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Bank A/C No.</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600; color: #111827;">${bankAccountNo}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">PF Number</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600; color: #111827;">${pfNumber}</td>
          </tr>

          <!-- Earnings / Deductions Header -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 700; background: #f3f4f6; color: #1B4B31;" colspan="2">
              EARNINGS
            </td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 700; background: #f3f4f6; color: #991b1b;" colspan="2">
              DEDUCTIONS
            </td>
          </tr>

          <!-- Basic & PF -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Basic Salary / Stipend</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right; font-weight: 600;">${inr(basic)}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Provident Fund (PF)</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right; font-weight: 600;">${inr(pf)}</td>
          </tr>

          <!-- HRA & PT -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">House Rent Allowance (HRA)</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(hra)}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Professional Tax (PT)</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(pt)}</td>
          </tr>

          <!-- Special Allowance & Income Tax -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Special Allowance</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(specialAllowance)}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Income Tax (TDS)</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(incomeTax)}</td>
          </tr>

          <!-- Travel Allowance & ESI -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Conveyance / Travel Allowance</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(travelAllowance)}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">ESI</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(esi)}</td>
          </tr>

          <!-- Bonus / Incentives & Attendance/LOP Deduction -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Bonus / Incentives</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(bonusIncentive)}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">
              <div>
                <strong style="color: #111827; font-size: 12px;">Attendance / LOP Deduction</strong>
                <div style="display: flex; flex-direction: column; gap: 3px; margin-top: 4px; font-size: 11px; color: #374151;">
                  <div>
                    Absent/LOP Days: <strong style="color: ${absentDays > 0 ? '#991b1b' : '#374151'};">${absentDays}</strong> <span style="color: #6b7280;">(@100% per-day)</span>
                  </div>
                  <div>
                    Unpaid Half-Days: <strong style="color: ${halfDayCount > 0 ? '#991b1b' : '#374151'};">${halfDayCount}</strong> <span style="color: #6b7280;">(@50% per-day)</span>
                  </div>
                </div>
              </div>
            </td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right; vertical-align: middle; color: ${attendanceLopDeduction > 0 ? '#991b1b' : 'inherit'}; font-weight: ${attendanceLopDeduction > 0 ? 700 : 'normal'};">
              ${inr(attendanceLopDeduction)}
            </td>
          </tr>

          <!-- Other Allowances & Other Deductions -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Other Allowance</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(otherAllowance)}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">Other Deductions</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; text-align: right;">${inr(otherDeductions)}</td>
          </tr>

          <!-- Totals -->
          <tr style="background: #f9fafb;">
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 700; color: #1B4B31;">Total Earnings :</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 700; color: #1B4B31; text-align: right;">${inr(gross)}</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 700; color: #991b1b;">Total Deductions :</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 700; color: #991b1b; text-align: right;">${inr(totalDeductions)}</td>
          </tr>

          <!-- Net Pay -->
          <tr style="background: #ecfdf5;">
            <td style="border: 1px solid #cbd5e1; padding: 9px 10px; font-size: 13px; font-weight: 800; color: #166534;" colspan="2">
              Net Payable Salary :
            </td>
            <td style="border: 1px solid #cbd5e1; padding: 9px 10px; font-size: 14px; font-weight: 800; color: #166534; text-align: right;" colspan="2">
              ${inr(net)}
            </td>
          </tr>
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Amount in Words :</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-style: italic; color: #374151;" colspan="3">
              ${numberToWordsIndian(net)} Rupees Only
            </td>
          </tr>

          <!-- Payment Details info row -->
          <tr style="background: #f9fafb;">
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Payment Status :</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">
              <span style="font-weight: 700; color: ${isPaid ? '#16a34a' : '#ca8a04'};">
                ${isPaid ? 'Paid' : 'Pending Payment'}
              </span>
            </td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px; font-weight: 600;">Payment Date :</td>
            <td style="border: 1px solid #cbd5e1; padding: 7px 10px; font-size: 12px;">${paidDate}</td>
          </tr>

          <!-- Signatures -->
          <tr>
            <td style="border: 1px solid #cbd5e1; padding: 12px 10px 8px; font-size: 12px; height: 75px; vertical-align: bottom;" colspan="2">
              <div style="font-weight: 700; margin-bottom: 24px;">Employer Signature :</div>
              <div style="font-style: italic; color: #555; font-size: 11px;">
                Authorized Signatory, Samriddhi Anveshana Pvt Ltd
              </div>
            </td>
            <td style="border: 1px solid #cbd5e1; padding: 12px 10px 8px; font-size: 12px; height: 75px; vertical-align: bottom;" colspan="2">
              <div style="font-weight: 700; margin-bottom: 30px;">Employee Signature :</div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: "#ffffff",
    });

    const imgData = canvas.toDataURL("image/png");
    const doc = new jsPDF({
      orientation: "portrait",
      unit: "mm",
      format: "a4",
    });

    const pageWidth = 210;
    const margin = 10;
    const contentWidth = pageWidth - margin * 2;
    const contentHeight = (canvas.height * contentWidth) / canvas.width;

    doc.addImage(imgData, "PNG", margin, margin, contentWidth, contentHeight);
    doc.save(`Payslip_${empName.replace(/\s+/g, '_')}_${month}_${year}.pdf`);
  } catch (err) {
    console.error("Error generating Payslip PDF:", err);
  } finally {
    document.body.removeChild(container);
  }
}
